import { useFragment } from '@apollo/client';
import { FeatureFlag } from '@ps/web-core-enums';
import type { EventId } from '@ps/web-sports-api-types';
import React from 'react';

import useFeature from '@swf/core/hooks/useFeature';
import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import type { EventTypeId } from '@swf/sports/types/eventType';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import { getIsEventTypeScoreboardEnabled } from '@swf/sports/utils/getIsEventTypeScoreboardEnabled';
import type { ScoreboardData } from '@swf/sports/utils/getScoreboardType';
import {
  getIsFootballScoreboard,
  getIsTennisScoreboard
} from '@swf/sports/utils/getScoreboardType';

import type { EventStatus } from '../EventStatus/hooks/useEventStatus';

import DefaultEventCard from './components/DefaultEventCard/DefaultEventCard';
import Football from './components/Football/Football';
import Loading from './components/Loading/Loading';
import Tennis from './components/Tennis/Tennis';
import Unavailable from './components/Unavailable/Unavailable';

export interface Props {
  eventId: EventId;
  eventTypeId: EventTypeId;
  eventTime: string;
  inPlay?: boolean;
  name: string;
  eventStatusOverride?: EventStatus;
  canTurnInPlay?: boolean;
}

const EventCard: React.FC<Props> = ({
  eventTypeId,
  eventId,
  name,
  eventTime,
  inPlay,
  eventStatusOverride,
  canTurnInPlay
}) => {
  const { isLoading, isError } = useLiveData();
  const { data, complete, missing } = useFragment<ScoreboardData>(
    getEventScoreboardFragment(eventTypeId, eventId)
  );
  const isScoreboardsEnabled = useFeature(FeatureFlag.ISPScoreboards);

  const isEventTypeScoreboardEnabled = getIsEventTypeScoreboardEnabled(eventTypeId);
  const isMissingData = missing || !complete;
  let scoreboard = null;

  if (isScoreboardsEnabled && isEventTypeScoreboardEnabled && inPlay) {
    if (isLoading) {
      scoreboard = (
        <Loading
          eventId={eventId}
          eventTime={eventTime}
          inPlay={inPlay}
          name={name}
          eventTypeId={eventTypeId}
        />
      );
    } else if (isError || isMissingData) {
      scoreboard = (
        <Unavailable
          name={name}
          eventTime={eventTime}
          eventTypeId={eventTypeId}
          eventId={eventId}
          hasDarkBackground
        />
      );
    } else if (getIsTennisScoreboard(eventTypeId, data)) {
      scoreboard = (
        <Tennis
          eventId={eventId}
          eventTime={eventTime}
          name={name}
          tennisMatch={data}
          hasDarkBackground
        />
      );
    } else if (getIsFootballScoreboard(eventTypeId, data)) {
      scoreboard = (
        <Football
          eventTime={eventTime}
          eventId={eventId}
          name={name}
          footballMatch={data}
          hasDarkBackground
        />
      );
    }
  }

  return (
    <>
      <DefaultEventCard
        eventTypeId={eventTypeId}
        title={name}
        canTurnInPlay={canTurnInPlay}
        inPlay={inPlay}
        eventTime={eventTime}
        eventStatusOverride={eventStatusOverride}
        titleOnly={Boolean(scoreboard)}
      />
      {scoreboard}
    </>
  );
};

export { EventCard };
